<template>
  <div class="fts-sidenav-module">
    <template v-for="(x, ind) in menus">
      <!-- module (group, menu) -->
      <div 
        class="module-wrapper" 
        :class="{ 'module-wrapper--open': x.open, 'active': isActive(x.id, true)} " 
        :style="moduleChild" 
        :key="x.id" 
        @click="openChildren(x)" 
        v-if="isGroup(x)"
      >
        <div class="module-prepend">
          <div class="module-toggle">
            <font-awesome-icon :icon="['fas', 'angle-right']" />
          </div>
        </div>

        <div class="module-info">
          <!-- emoji -->
          <div class="module-emoji-wrapper" v-if="!!x.emoji">
            <fts-moji 
              :size="14" 
              :emoji="x.emoji" 
              class="module-emoji" 
            />
          </div>
          <!-- name -->
          <fts-tippy
            trigger-class="module-name"
            :options="{ placement: 'top-start' }"
            :content="x.name"
            class="module-name-wrapper"
          >
            <template #trigger>{{x.name}}</template>
          </fts-tippy>
          <!-- profile -->
          <div class="module-tag" v-if="x.isProfile">
            <font-awesome-icon :icon="['fas', 'user']" fixed-width />
          </div>
          <!-- actions -->
          <div class="module-action-container" v-if="isDesignMode && x.module">
            <fts-tippy 
              content="Open mini studio" 
              append-trigger-class="module-action" 
              :stop-propagation="true"
              :prevent-default="true"
              @trigger:click="openMiniStudio(x.id)"
            >
              <span class="material-icons-outlined">edit</span>
            </fts-tippy>

            <fts-tippy 
              content="New view" 
              append-trigger-class="module-action" 
              :stop-propagation="true"
              :prevent-default="true"
              @trigger:click="toggleView(
                openMiniStudio(x.id),
                { new: true }
              )"
            >
              <span class="material-icons-outlined">add</span>
            </fts-tippy>
          </div>
        </div>
      </div>
      <!-- custom (script) -->
      <div
        class="module-wrapper"
        :style="moduleChild"
        :key="x.id"
        @click="runScript(x)"
        v-else-if="x.script"
      >
        <div class="module-info">
          <!-- emoji -->
          <div class="module-emoji-wrapper" v-if="!!x.emoji">
            <fts-moji 
              :size="14" 
              :emoji="x.emoji" 
              class="module-emoji" 
            />
          </div>
          <!-- name -->
          <fts-tippy
            trigger-class="module-name"
            :options="{ placement: 'top-start' }"
            :content="x.name"
            class="module-name-wrapper"
          >
            <template #trigger>{{x.name}}</template>
          </fts-tippy>
        </div>
      </div>
      <!-- module / menu -->
      <router-link 
        :to="listViewRoute(x.id)" 
        custom 
        v-slot="{ navigate, href, route }" 
        class="module-wrapper" 
        :class="[
          {'module-wrapper--admin': x.id == 'everything'},
          {'active': isActive(x.id)}
        ]"
        :style="moduleChild"
        :key="x.id"
        v-else
      >
        <a :href="href" @click.prevent="openListView(navigate, route)">
          <div class="module-info">
            <template v-if="x.id == 'everything'">
              <div class="module-emoji-wrapper" v-if="!!x.emoji">
                <fts-moji 
                  :size="14" 
                  :emoji="x.emoji" 
                  class="module-emoji" 
                />
              </div>

              <fts-tippy
                trigger-class="module-name"
                :options="{ placement: 'top-start' }"
                content="For admin only"
                class="module-name-wrapper"
                v-if="x.id == 'everything'"
              >
                <template #trigger>{{x.name}}</template>
              </fts-tippy>
            </template>

            <template v-else>
              <div class="module-emoji-wrapper" v-if="!!x.emoji">
                <fts-moji 
                  :size="14" 
                  :emoji="x.emoji" 
                  class="module-emoji" 
                  v-if="!!x.emoji"
                />
              </div>

              <fts-tippy
                trigger-class="module-name"
                :options="{ placement: 'top-start' }"
                :content="x.name"
                class="module-name-wrapper"
              >
                <template #trigger>{{x.name}}</template>
              </fts-tippy>
            </template>

            <div class="module-tag" v-if="x.isProfile">
              <font-awesome-icon :icon="['fas', 'user']" fixed-width />
            </div>
            <!-- actions:module without listing or listing -->
            <div class="module-action-container" v-if="isDesignMode">
              <fts-tippy 
                content="Open mini studio" 
                append-trigger-class="module-action" 
                :stop-propagation="true"
                :prevent-default="true"
                @trigger:click="openMiniStudio(x.id)" 
                v-if="x.module"
              >
                <span class="material-icons-outlined">edit</span>
              </fts-tippy>

              <fts-tippy 
                content="Edit view" 
                append-trigger-class="module-action" 
                :stop-propagation="true"
                :prevent-default="true"
                @trigger:click="toggleView(
                  navigateRoute(route),
                  { listingId: x.id }
                )" 
                v-else
              >
                <span class="material-icons-outlined">edit</span>
              </fts-tippy>

              <fts-tippy
                content="New view" 
                append-trigger-class="module-action" 
                :stop-propagation="true"
                :prevent-default="true"
                @trigger:click="toggleView(
                  navigateRoute(route),
                  { new: true }
                )"
              >
                <span class="material-icons-outlined">add</span>
              </fts-tippy>
            </div>
          </div>
        </a>
      </router-link>
      <!-- child -->
      <fts-sidenav-module
        :mode="mode"
        child
        :modules="x.children"
        :deep="deep + 1"
        :parent-id="getParentId(x)"
        :is-design-mode="isDesignMode"
        v-if="x.open"
        :key="`${x.id || ind}-child`"
      />
    </template>
  </div>
</template>

<script>
import { authorizationStore } from '../store/authorizationStore';
import { clientStore } from '../store/clientStore';
import commons from '../commons/commons';
import { SYSTEM_CAPABILITY, isCapableOf } from '../commons/flags';

import FTSBtn from '../elements/FTSBtn';
import FTSContenteditable from '../elements/FTSContenteditable';
import FTSEmoji from '../elements/FTSEmoji';
import FTSMoji from '../elements/FTSMoji';
import FTSReleaseBadge from '../elements/FTSReleaseBadge';
import FTSTippy from '../elements/FTSTippy';

export default {
  name: "fts-sidenav-module",
  props: {
    mode: String,
    modules: Array,
    deep: {
      type: Number,
      default: 0,
    },
    child: Boolean,
    parentId: [String, Number],
    action: Boolean,
    filter: String,
    isDesignMode: Boolean
  },
  components: {
    "fts-btn": FTSBtn,
    "fts-contenteditable": FTSContenteditable,
    "fts-emoji": FTSEmoji,
    "fts-moji": FTSMoji,
    "fts-release-badge": FTSReleaseBadge,
    "fts-tippy": FTSTippy
  },
  data: () => ({
    filterUpdated: false,
    filterOpenMenuIds: []
  }),
  methods: {
    isCreatable(moduleId) {
      const modules = clientStore.state.workspaceModules;
      const module = modules.find(x => x.id == moduleId);
      return isCapableOf(SYSTEM_CAPABILITY.Write, modules, moduleId) && module && module.canCreate;
    },
    isGroup(x) {
      return (
        x.type === "group" ||
        (x.children && x.children.length && x.children[0].default !== true)
      );
    },
    isActive(itemId, isModule /* proceed logic for module (ministudio) */) {
      if(isModule){
        const routeModuleId =
          this.$route.name === 'lite-studio' &&
          this.$route.params &&
          this.$route.params.moduleId
            ? commons.decodeids(this.$route.params.moduleId)
            : null;
        const activeModuleId = this.list.parentId || this.moduleId || routeModuleId;
        const isMatchedModule = itemId == activeModuleId;
        const isEverything = this.list.id === 'everything' || (
          this.$route.name === 'lite-studio' &&
          !(this.$route.query && this.$route.query.l && this.$route.query.l !== 'everything')
        );

        return isMatchedModule && isEverything;
      }

      const currentTarget = this.parentId ? `${this.parentId}-${itemId}` : `${itemId}`;
      const target = this.list.id ? `${this.module.id}-${this.list.id}` : `${this.module.id}`;
      return currentTarget == target;
    },
    filterTree(items, keyword, forceOpen) {
      const result = [];

      for (const item of items) {
        const selfMatch = item.name && item.name.toLowerCase().includes(keyword);

        let filteredChildren = [];
        let isOpen = item.open;
        if (item.children && item.children.length) {
          filteredChildren = this.filterTree(item.children, keyword, forceOpen);
        }

        if (!selfMatch && !filteredChildren.length) continue;

        if (forceOpen) {
          isOpen = true;
          this.filterOpenMenuIds.push(item.id);
        } else if (this.filterOpenMenuIds.some(x => x == item.id)) {
          isOpen = true; 
        }

        result.push({
          ...item,
          open: isOpen,
          children: selfMatch ? item.children : filteredChildren
        });
      }

      return result;
    },
    openChildren(node) {
      if (commons.elementStopPropagation()) return;

      const menus = _.cloneDeep(clientStore.state.workspace.menus);

      function findDeep(items, id) {
        for (const item of items) {
          if (item.id === id) return item;
          if (item.children) {
            const found = findDeep(item.children, id);
            if (found) return found;
          }
        }
        return null;
      }

      const target = findDeep(menus, node.id);
      if (target) {
        target.open = !node.open;
        this.filterOpenMenuIds = this.filterOpenMenuIds.filter(x => x != target.id);
        clientStore.dispatch("setWorkspaceMenus", menus);
      }
    },
    runScript(x) {
      eval(x.script);
    },
    getParentId(x) {
      if (x.type !== "group") return x.id;
    },
    listViewRoute(x) {
      if (this.mode === "design")
        return;
      else if (this.parentId)
        return {
          name: 'lite',
          params: { moduleId: commons.encodeids(this.parentId) },
          query: { l: x }
        }
      else
        return {
          name: 'lite',
          params: { moduleId: commons.encodeids(x) }
        }
    },
    designRoute(moduleId, listId) {
      let route = {
        name: 'studio',
        query: { id: commons.encodeids(moduleId) }
      }

      if (listId)
        route.query = {
          ...route.query,
          t: 'menu',
          tid: listId
        }

      return route;
    },
    openListView(navigate, route) {
      if (commons.elementStopPropagation()) {
        return;
      } else {
        navigate(route);
      }
    },
    openCreateForm(item) {
      if (this.$ftsDetailView) {
        window.eventBus.$emit("detailView:form", {
          moduleId: item.id,
          listingId: Array.isArray(item.children) && item.children[0] && item.children[0].id,
          activityId: "create"
        });
      } else {
        window.eventBus.$emit("quickViewData", {
          activityId: "create",
          vectorId: item.id,
        });
      }
    },
    openListDesign(x) {
      if (this.mode == "design") {
        this.$delete(x, "hover");
        this.$emit("list", x.id);
      }
    },
    routeTo(route) { 
      if (route) {
        this.$router.push(route);
      } 
    },
    async toggleView(func, args = {}){ 
      await func; 
      await this.$nextTick();
      window.eventBus.$emit("open-designer", args); 
    },
    async navigateRoute(route) {
      const resolvedRoute = this.$router.resolve(route).route;

      if (resolvedRoute.fullPath !== this.$route.fullPath) {
        await this.$router.push(route);
      }
    },
    collapseAllMenus() {
      let menus = _.cloneDeep(clientStore.state.workspace.menus);
      commons.collapseAllSidebarMenu(menus);
      clientStore.dispatch("setWorkspaceMenus", menus);
      this.filterOpenMenuIds = [];
    },
    async openMiniStudio(moduleId) {
      await this.navigateRoute({
        name: "lite-studio",
        params: { moduleId: commons.encodeids(moduleId) }
      });
    }
  },
  watch: {
    filter() {
      this.filterUpdated = true;
      this.filterOpenMenuIds = [];
    }
  },
  computed: {
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    menus() {
      let menus = this.child ? this.modules : commons.visibleMenu(clientStore.state.workspace.menus);
      let filteredMenus = menus.filter(m => m.id !== 'everything');

      if (this.child || !this.filter || this.filter == '') {
        return filteredMenus;
      }

      let forceOpen = false;

      if (this.filterUpdated) {
        forceOpen = true;
        this.filterUpdated = false;
      }

      return this.filterTree(filteredMenus, this.filter.toLowerCase(), forceOpen);
    },
    moduleChild() {
      return this.child && { 'padding-left': `${(this.deep * 20) + 20}px` };
    },
    module() {
      return clientStore.state.module || {};
    },
    moduleId() {
      return clientStore.getters.moduleId;
    },
    list() {
      return clientStore.state.list || {};
    }
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/vars.scss" as *;

.fts-sidenav-module {
  .module-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    margin-left: 8px;
    margin-right: 8px;
    margin-bottom: 3px;
    padding-top: 6px;
    padding-bottom: 6px;
    padding-left: 20px;
    padding-right: 12px;
    border-radius: var(--inistate-rounded-8);
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-label-text-14--line-height);
    text-decoration: unset;
    color: var(--inistate-text-primary-color);
    cursor: pointer;
    .module-info {
      min-width: 0;
      flex: 1;
      display: flex;
      align-items: center;
      cursor: inherit;

      @mixin module-prefix-wrapper {
        flex-shrink: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 0.375rem;
      }

      .module-emoji-wrapper {
        @include module-prefix-wrapper;
      }
      .module-emoji {
        cursor: inherit;
      }
      .module-name-wrapper {
        min-width: 0;
        min-height: 24px;
        display: flex;
        align-items: center;
        cursor: inherit;
        .module-name {
          @include text-overflow-ellipsis;
          cursor: inherit;
        }
      }
      .module-contenteditable {
        margin-right: 0.5rem;
      }
      .module-tag {
        margin-left: 0.375rem;
        padding: 0.1rem 0.125rem;
        font-size: 0.625rem;
        border-radius: 0.25rem;
        background-color: var(--drawer-tag-background, var(--drawer-secondary-background));
        color: var(--drawer-tag, var(--drawer-secondary));
      }
    }
    .module-prepend {
      position: absolute;
      left: unset;
      width: 16px;
      height: 16px;
      transform: translateX(-18px);
    }
    .module-toggle {
      width: inherit;
      height: inherit;
      display: none;
      border-radius: var(--inistate-rounded-4);
      font-size: var(--inistate-label-text-12);
      color: var(--inistate-color-neutral-500);
      transition: transform 0.3s ease;
    }
    .module-action-container {
      flex: 1;
      display: none;
      margin-left: 6px;
      > *:not(:last-child) {
        margin-right: 2px;
      }
    }
    .module-action {
      height: 24px;
      width: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--inistate-rounded-4);
      background-color: transparent;
      color: var(--inistate-color-neutral-500);
      transition: background-color 0.3s ease, color 0.3s ease;
      > span {
        font-size: var(--inistate-label-text-16);
      }
      &:hover {
        background-color: var(--inistate-color-shade-white);
        color: var(--inistate-text-primary-color);
        cursor: pointer;
      }
    }
    &:hover {
      background-color: var(--inistate-color-primary-50);
      > .module-info {
        font-weight: var(--inistate-font-weight-medium);
        & > .module-action-container {
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }
      }
      > .module-prepend > .module-toggle {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
    &.active {
      background-color: var(--inistate-color-neutral-200);
      > .module-info {
        font-weight: var(--inistate-font-weight-medium);
      }
    }
    &.module-wrapper--open {
      .module-toggle {
        transform: rotate(90deg);
      }
    }
    &.module-wrapper--admin {
      color: var(--fts-secondary);
      &:hover,
      &.active {
        &::before {
          content: "";
          position: absolute;
          inset: 0;
          background-color: var(--fts-card-background);
          border-radius: inherit;
          z-index: -1;
        }
      }
      &:hover {
        background-color: rgba(var(--fts-secondary-rgb), 0.09);
      }
      &.active {
        background-color: rgba(var(--fts-secondary-rgb), 0.18);
      }
    }
  }
}
</style>
